import React, { useState, useContext, useEffect, useRef } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";
import useSettings from "../../hooks/useSettings";
import IconButton from "@material-ui/core/IconButton";
import Brightness4Icon from "@material-ui/icons/Brightness4";
import Brightness7Icon from "@material-ui/icons/Brightness7";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import InputAdornment from "@material-ui/core/InputAdornment";
import { Helmet } from "react-helmet";
// Importar bandeiras do diretório public
const BRFlag = '/flags/br.png';
const USFlag = '/flags/us.png';
const ESFlag = '/flags/es.png';
const ARFlag = '/flags/sa.png';
const TRFlag = '/flags/tr.png';
import PublicLogo from "../../components/PublicLogo";
import clsx from "clsx";
import { getBackendUrl } from "../../config";

const languageOptions = [
  { value: "pt", label: "Português", icon: BRFlag },
  { value: "en", label: "English", icon: USFlag },
  { value: "es", label: "Español", icon: ESFlag },
  { value: "ar", label: "العربية", icon: ARFlag },
  { value: "tr", label: "Türkçe", icon: TRFlag },
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    padding: "0",
    margin: "0",
    boxSizing: "border-box",
    overflow: "hidden",
    position: "relative",
    
    // Background dinámico basado en el tema
    background: theme.palette.type === 'dark' 
      ? "linear-gradient(135deg, #0f172a 0%, #1e293b 30%, #334155 70%, #475569 100%)"
      : "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #f093fb 75%, #f5576c 100%)",
    
    // Patrón de fondo animado
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundImage: theme.palette.type === 'dark'
        ? `
          radial-gradient(circle at 20% 20%, rgba(99, 102, 241, 0.1) 1px, transparent 1px),
          radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.1) 1px, transparent 1px),
          radial-gradient(circle at 40% 60%, rgba(59, 130, 246, 0.05) 1px, transparent 1px)
        `
        : `
          radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
          radial-gradient(circle at 75% 75%, rgba(255,255,255,0.1) 1px, transparent 1px),
          radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05) 1px, transparent 1px)
        `,
      backgroundSize: "60px 60px, 80px 80px, 40px 40px",
      animation: "$float 25s ease-in-out infinite",
    },
    
    // Efecto de partículas flotantes
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundImage: theme.palette.type === 'dark'
        ? `
          radial-gradient(circle at 10% 90%, rgba(168, 85, 247, 0.03) 2px, transparent 2px),
          radial-gradient(circle at 90% 10%, rgba(34, 197, 94, 0.03) 2px, transparent 2px)
        `
        : `
          radial-gradient(circle at 15% 85%, rgba(236, 72, 153, 0.05) 2px, transparent 2px),
          radial-gradient(circle at 85% 15%, rgba(59, 130, 246, 0.05) 2px, transparent 2px)
        `,
      backgroundSize: "120px 120px, 100px 100px",
      animation: "$floatReverse 30s ease-in-out infinite",
    },
  },

  "@keyframes float": {
    "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
    "33%": { transform: "translateY(-15px) rotate(120deg)" },
    "66%": { transform: "translateY(-8px) rotate(240deg)" },
  },

  "@keyframes floatReverse": {
    "0%, 100%": { transform: "translateY(0px) rotate(0deg)" },
    "50%": { transform: "translateY(10px) rotate(-180deg)" },
  },

  // Container responsivo mejorado
  containerLogin: {
    padding: "20px",
    maxWidth: "480px",
    width: "100%",
    margin: "0 auto",
    position: "relative",
    zIndex: 10,
    
    [theme.breakpoints.up("md")]: {
      maxWidth: "450px",
      padding: "24px",
    },
    
    [theme.breakpoints.down("sm")]: {
      padding: "16px",
      maxWidth: "100%",
    },
  },

  paper: {
    backgroundColor: theme.palette.type === 'dark' 
      ? "rgba(24, 27, 34, 0.96)" 
      : "rgba(255, 255, 255, 0.98)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    boxShadow: theme.palette.type === 'dark'
      ? `
        0 25px 50px rgba(0, 0, 0, 0.4),
        0 0 0 1px rgba(148, 163, 184, 0.1),
        inset 0 1px 0 rgba(148, 163, 184, 0.1)
      `
      : `
        0 25px 50px rgba(0, 0, 0, 0.15),
        0 0 0 1px rgba(255, 255, 255, 0.2),
        inset 0 1px 0 rgba(255, 255, 255, 0.3)
      `,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "40px 36px 32px",
    borderRadius: "8px",
    maxWidth: "450px",
    width: "100%",
    margin: "0 auto",
    border: theme.palette.type === 'dark'
      ? "1px solid rgba(148, 163, 184, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.2)",
    animation: "$slideInScale 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    
    // Efecto de brillo sutil
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: theme.palette.type === 'dark'
        ? "linear-gradient(90deg, transparent, rgba(148, 163, 184, 0.3), transparent)"
        : "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent)",
      borderRadius: "8px 8px 0 0",
    },
    
    [theme.breakpoints.down("sm")]: {
      animation: "$slideInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1)",
      borderRadius: "8px",
      padding: "36px 28px",
      margin: "16px",
    },
    
    [theme.breakpoints.down("xs")]: {
      padding: "30px 22px",
      margin: "12px",
    },
  },

  formTitle: {
    color: theme.palette.type === "dark" ? "#f8fafc" : "#172033",
    fontSize: "1.25rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },

  formSubtitle: {
    color: theme.palette.type === "dark" ? "#aeb7c7" : "#667085",
    fontSize: "0.875rem",
    marginBottom: theme.spacing(2),
  },

  "@keyframes slideInScale": {
    from: {
      opacity: 0,
      transform: "scale(0.9) translateY(20px)",
    },
    to: {
      opacity: 1,
      transform: "scale(1) translateY(0)",
    },
  },

  "@keyframes slideInUp": {
    from: {
      opacity: 0,
      transform: "translateY(40px)",
    },
    to: {
      opacity: 1,
      transform: "translateY(0)",
    },
  },

  "@keyframes pulse": {
    "0%, 100%": { 
      boxShadow: theme.palette.type === 'dark'
        ? "0 0 0 0 rgba(99, 102, 241, 0.4)"
        : "0 0 0 0 rgba(59, 130, 246, 0.4)"
    },
    "50%": { 
      boxShadow: theme.palette.type === 'dark'
        ? "0 0 0 10px rgba(99, 102, 241, 0)"
        : "0 0 0 10px rgba(59, 130, 246, 0)"
    },
  },

  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark' ? "#6366f1" : "#3b82f6",
  },

  form: {
    width: "100%",
    marginTop: theme.spacing(2),
  },

  submit: {
    margin: theme.spacing(3, 0, 2),
    background: theme.palette.primary.main,
    color: "white",
    borderRadius: "8px",
    padding: "14px 0",
    fontSize: "16px",
    fontWeight: 600,
    textTransform: "none",
    boxShadow: theme.palette.type === 'dark'
      ? "0 8px 25px rgba(99, 102, 241, 0.3)"
      : "0 8px 25px rgba(102, 126, 234, 0.3)",
    border: "none",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
      transition: "left 0.6s",
    },
    
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateY(-1px)",
      boxShadow: theme.palette.type === 'dark'
        ? "0 12px 35px rgba(99, 102, 241, 0.4)"
        : "0 12px 35px rgba(102, 126, 234, 0.4)",
      
      "&::before": {
        left: "100%",
      },
    },
    
    "&:active": {
      transform: "translateY(-1px) scale(1.01)",
    },
    
    "&:focus": {
      outline: "none",
      animation: "$pulse 2s infinite",
    },
  },

  powered: {
    color: "white",
  },

  // Logo - agora usando DynamicLogo
  logoImg: {
    // Estilos movidos para o componente DynamicLogo inline
  },

  iconButton: {
    position: "absolute",
    top: 20,
    right: 20,
    background: theme.palette.type === 'dark'
      ? "rgba(148, 163, 184, 0.1)"
      : "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(15px)",
    border: theme.palette.type === 'dark'
      ? "1px solid rgba(148, 163, 184, 0.2)"
      : "1px solid rgba(255, 255, 255, 0.25)",
    color: theme.palette.type === 'dark' ? "#e2e8f0" : "#374151",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    borderRadius: "8px",
    width: "42px",
    height: "42px",
    
    "&:hover": {
      background: theme.palette.type === 'dark'
        ? "rgba(148, 163, 184, 0.2)"
        : "rgba(255, 255, 255, 0.25)",
      transform: "translateY(-1px)",
      boxShadow: theme.palette.type === 'dark'
        ? "0 8px 25px rgba(0, 0, 0, 0.3)"
        : "0 8px 25px rgba(0, 0, 0, 0.1)",
    },
    
    "&:active": {
      transform: "translateY(0)",
    },
  },

  // Campos de entrada mejorados con soporte para tema
  textField: {
    marginBottom: theme.spacing(1),
    
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      backgroundColor: theme.palette.type === 'dark'
        ? "rgba(51, 65, 85, 0.6)"
        : "rgba(255, 255, 255, 0.8)",
      backdropFilter: "blur(15px)",
      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
      color: theme.palette.type === 'dark' ? "#f1f5f9" : "#1f2937",
      
      "&:hover": {
        backgroundColor: theme.palette.type === 'dark'
          ? "rgba(51, 65, 85, 0.8)"
          : "rgba(255, 255, 255, 0.95)",
        transform: "translateY(-1px)",
        boxShadow: theme.palette.type === 'dark'
          ? "0 8px 25px rgba(0, 0, 0, 0.2)"
          : "0 8px 25px rgba(0, 0, 0, 0.08)",
      },
      
      "&.Mui-focused": {
        backgroundColor: theme.palette.type === 'dark'
          ? "rgba(51, 65, 85, 0.9)"
          : "rgba(255, 255, 255, 1)",
        boxShadow: theme.palette.type === 'dark'
          ? "0 0 0 3px rgba(99, 102, 241, 0.2), 0 8px 25px rgba(0, 0, 0, 0.2)"
          : "0 0 0 3px rgba(102, 126, 234, 0.15), 0 8px 25px rgba(0, 0, 0, 0.08)",
        transform: "translateY(-1px)",
      },
      
      "& input": {
        color: theme.palette.type === 'dark' ? "#f1f5f9" : "#1f2937",
        fontSize: "16px",
        fontWeight: 500,
        padding: "16px 14px",
        
        "&::placeholder": {
          color: theme.palette.type === 'dark' ? "#94a3b8" : "#9ca3af",
          opacity: 1,
        },
      },
      
      "& fieldset": {
        borderColor: theme.palette.type === 'dark'
          ? "rgba(148, 163, 184, 0.2)"
          : "rgba(102, 126, 234, 0.2)",
        borderWidth: "2px",
      },
      
      "&:hover fieldset": {
        borderColor: theme.palette.type === 'dark'
          ? "rgba(148, 163, 184, 0.4)"
          : "rgba(102, 126, 234, 0.4)",
      },
      
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.type === 'dark' ? "#6366f1" : "#667eea",
        borderWidth: "2px",
      },
    },
    
    "& .MuiInputLabel-root": {
      color: theme.palette.type === 'dark' ? "#94a3b8" : "#6b7280",
      fontWeight: 600,
      fontSize: "14px",
      
      "&.Mui-focused": {
        color: theme.palette.type === 'dark' ? "#6366f1" : "#667eea",
      },
    },
  },

  // Seletor de idioma - versão melhorada
  languageSelector: {
    position: "fixed",
    top: "30px",
    left: "30px",
    zIndex: 1000,
    minWidth: 140,
    background: theme.palette.type === 'dark'
      ? "rgba(51, 65, 85, 0.8)"
      : "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(15px)",
    borderRadius: "8px",
    border: theme.palette.type === 'dark'
      ? "1px solid rgba(148, 163, 184, 0.2)"
      : "1px solid rgba(102, 126, 234, 0.2)",
    padding: "12px 16px",
    boxShadow: theme.palette.type === 'dark'
      ? "0 8px 25px rgba(0, 0, 0, 0.3)"
      : "0 8px 25px rgba(0, 0, 0, 0.1)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: theme.palette.type === 'dark'
        ? "0 12px 35px rgba(0, 0, 0, 0.4)"
        : "0 12px 35px rgba(0, 0, 0, 0.15)",
    },
  },

  // Link de registro
  registerLink: {
    color: "#3b82f6",
    textDecoration: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    "&:hover": {
      color: "#2563eb",
      textDecoration: "underline",
    },
  },

  // Estilos para o dropdown de idiomas
  languageDropdown: {
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
    color: theme.mode === "light" ? "#1f2937" : "white",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    gap: "8px",
    transition: "opacity 0.3s ease",
    "&:hover": {
      opacity: 0.8,
    },
  },

  languageOptions: {
    position: "absolute",
    top: "100%",
    left: "0",
    marginTop: "8px",
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    borderRadius: "8px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    padding: "8px",
    zIndex: 1000,
    minWidth: "140px",
  },

  languageOption: {
    background: "none",
    border: "none",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    padding: "8px 12px",
    textAlign: "left",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "rgba(59, 130, 246, 0.1)",
      color: "#3b82f6",
    },
  },

  flagIcon: {
    width: 20,
    height: 15,
    borderRadius: 2,
  },

  // Estilos adicionales para mejorar la experiencia
  passwordToggle: {
    color: theme.palette.type === 'dark' ? "#94a3b8" : "#6b7280",
    transition: "all 0.3s ease",
    
    "&:hover": {
      color: theme.palette.type === 'dark' ? "#6366f1" : "#667eea",
      transform: "scale(1.1)",
    },
  },

  forgotPassword: {
    color: theme.palette.type === 'dark' ? "#94a3b8" : "#6b7280",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: 500,
    transition: "all 0.3s ease",
    
    "&:hover": {
      color: theme.palette.type === 'dark' ? "#6366f1" : "#667eea",
      textDecoration: "underline",
    },
  },

  signupLink: {
    color: theme.palette.type === 'dark' ? "#6366f1" : "#667eea",
    textDecoration: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    
    "&:hover": {
      color: theme.palette.type === 'dark' ? "#8b5cf6" : "#5b21b6",
      textDecoration: "underline",
    },
  },

  loadingOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.palette.type === 'dark'
      ? "rgba(15, 23, 42, 0.8)"
      : "rgba(255, 255, 255, 0.8)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "24px",
    zIndex: 10,
  },
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const { appLogoFavicon, appName, mode } = colorMode;
  const [user, setUser] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [allowSignup, setAllowSignup] = useState(false);
  const { getPublicSetting } = useSettings();
  const { handleLogin } = useContext(AuthContext);

  const [open, setOpen] = useState(false);
  const ref = useRef();
  const [enabledLanguages, setEnabledLanguages] = useState(["pt", "en", "es", "ar", "tr"]);
  const [backgroundLight, setBackgroundLight] = useState("");
  const [backgroundDark, setBackgroundDark] = useState("");

  const getCompanyIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const companyId = urlParams.get("companyId");
    return companyId ? parseInt(companyId) : null;
  };

  const handleChangeInput = (e) => {
    setUser({ ...user, [e.target.name]: e.target.value });
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  useEffect(() => {
    let isMounted = true;
    const companyId = getCompanyIdFromUrl();

    getPublicSetting("userCreation", companyId)
      .then((data) => {
        if (isMounted) {
          setAllowSignup(data === "enabled");
        }
      })
      .catch((error) => {
        console.log("Error reading setting", error);
      });
    
    getPublicSetting("enabledLanguages", companyId)
      .then((langs) => {
        if (isMounted) {
          let arr = ["pt", "en", "es", "ar", "tr"];
          try {
            if (langs) {
              const parsed = JSON.parse(langs);
              // Mapear pt-BR para pt se necessário
              arr = parsed.map(lang => lang === "pt-BR" ? "pt" : lang);
            }
          } catch {}
          setEnabledLanguages(arr);
        }
      })
      .catch(() => {
        if (isMounted) {
          setEnabledLanguages(["pt", "en", "es", "ar", "tr"]);
        }
      });

    getPublicSetting("appLogoBackgroundLight", companyId)
      .then((bgLight) => {
        if (isMounted) {
          if (bgLight) {
            setBackgroundLight(getBackendUrl() + "/public/" + bgLight);
          } else {
            setBackgroundLight("");
          }
        }
      })
      .catch(() => {
        if (isMounted) {
          setBackgroundLight("");
        }
      });

    getPublicSetting("appLogoBackgroundDark", companyId)
      .then((bgDark) => {
        if (isMounted) {
          if (bgDark) {
            setBackgroundDark(getBackendUrl() + "/public/" + bgDark);
          } else {
            setBackgroundDark("");
          }
        }
      })
      .catch(() => {
        if (isMounted) {
          setBackgroundDark("");
        }
      });
      
    return () => {
      isMounted = false;
    };
  }, []);

  // Mapear pt-BR para pt para compatibilidade
  const currentLang = i18n.language === "pt-BR" ? "pt" : i18n.language;
  const current =
    languageOptions.find((opt) => opt.value === currentLang) ||
    languageOptions[0];

  const handleSelect = (opt) => {
    i18n.changeLanguage(opt.value);
    localStorage.setItem("i18nextLng", opt.value);
    localStorage.setItem("language", opt.value);
    setOpen(false);
    window.location.reload();
  };

  let finalBackground;
  if (mode === "light") {
    if (backgroundLight) {
      finalBackground = `url(${backgroundLight})`;
    } else {
      finalBackground = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #111827 100%)`;
    }
  } else {
    if (backgroundDark) {
      finalBackground = `url(${backgroundDark})`;
    } else {
      finalBackground = "linear-gradient(135deg, #111827 0%, #1f2937 100%)";
    }
  }

  finalBackground = String(finalBackground || "#f5f5f5");


  return (
    <>
      <Helmet>
        <title>{appName || "Multi100"}</title>
        <link rel="icon" href={appLogoFavicon || "/default-favicon.ico"} />
      </Helmet>
      
      <div className={clsx(classes.root, "login-page")}
      style={{   
          width: "100vw !important",
          height: "100vh !important",
          display: "flex !important",
          alignItems: "center !important",
          justifyContent: "center !important",
          padding: "0 !important",
          margin: "0 !important",
          boxSizing: "border-box !important",
          overflow: "auto !important",
          backgroundColor: "#111827",
          backgroundImage:
            typeof finalBackground === "string" &&
            (finalBackground.includes("url(") || finalBackground.includes("gradient("))
              ? finalBackground
              : "none",
          backgroundRepeat: "no-repeat !important",
          backgroundSize: "cover !important",
          backgroundPosition: "center !important",
        }}
      >
        {/* Seletor de idioma */}
        <div
          ref={ref}
          className={classes.languageSelector}
        >
          <button 
            onClick={() => setOpen((o) => !o)}
            className={classes.languageDropdown}
          >
            <img
              src={current.icon}
              alt={current.label}
              className={classes.flagIcon}
            />
            {current.label}
            <span>▾</span>
          </button>

          {open && (
            <div className={classes.languageOptions}>
              {languageOptions
                .filter((opt) => enabledLanguages.includes(opt.value))
                .map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleSelect(opt)}
                    className={classes.languageOption}
                  >
                    <img
                      src={opt.icon}
                      alt={opt.label}
                      className={classes.flagIcon}
                    />
                    {opt.label}
                  </button>
                ))}
            </div>
          )}
        </div>

        <Container
          component="main"
          maxWidth="xs"
          className={classes.containerLogin}
        >
          <CssBaseline />
          <div className={classes.paper}>
            <IconButton
              className={classes.iconButton}
              onClick={colorMode.toggleColorMode}
            >
              {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            
            <div>
              <PublicLogo 
                className={classes.logoImg}
                style={{
                  width: "100%",
                  maxWidth: "280px",
                  height: "auto",
                  maxHeight: "80px",
                  margin: "0 auto 20px auto",
                  filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.1))"
                }}
                alt="logo" 
              />
            </div>
            <Typography className={classes.formTitle}>
              {i18n.t("login.title")}
            </Typography>
            <Typography className={classes.formSubtitle}>
              {appName || "Multi100"}
            </Typography>
            
            <form className={classes.form} noValidate onSubmit={handlSubmit}>
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                id="email"
                label={i18n.t("login.form.email")}
                name="email"
                value={user.email}
                onChange={handleChangeInput}
                autoComplete="email"
                autoFocus
                className={classes.textField}
              />
              <TextField
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label={i18n.t("login.form.password")}
                type={showPassword ? "text" : "password"}
                id="password"
                value={user.password}
                onChange={handleChangeInput}
                autoComplete="current-password"
                className={classes.textField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className={classes.passwordToggle}
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submit}
              >
                {i18n.t("login.buttons.submit")}
              </Button>
              {allowSignup && (
                <Grid container justifyContent="center">
                  <Grid item>
                    <Link
                      href="#"
                      variant="body2"
                      component={RouterLink}
                      to="/signup"
                      className={classes.signupLink}
                    >
                      {i18n.t("login.buttons.register")}
                    </Link>
                  </Grid>
                </Grid>
              )}
            </form>
          </div>
        </Container>
      </div>
    </>
  );
};

export default Login;
